import { useState, useRef, useEffect, useCallback } from 'react';
import { buildSyncPlan, executeSyncPlan, pushMap } from '../utils/mapSync';

export function useMapSync({ user, currentMapId, reloadCurrentMap, refreshMaps, switchMap }) {
  const [mergePlan, setMergePlan] = useState(null);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const prevUserRef = useRef(null);

  // Sync on login — triggered when user goes from null → logged in
  useEffect(() => {
    if (user && !prevUserRef.current) {
      buildSyncPlan(user.id)
        .then(async plan => {
          // Upload silenzioso: mappe locali non ancora su cloud, nessuna interazione richiesta
          plan.toUpload.forEach(meta => pushMap(meta.id, user.id).catch(console.error));

          // Elimina silenziosamente le mappe cancellate su cloud da un altro device
          if (plan.toDeleteLocally.length > 0) {
            const { deleteMap: deleteLocalMap } = await import('../utils/storage');
            plan.toDeleteLocally.forEach(meta => deleteLocalMap(meta.id));
            refreshMaps();
            const currentWasDeleted = plan.toDeleteLocally.some(m => m.id === currentMapId);
            if (currentWasDeleted) {
              const { getMapIndex } = await import('../utils/storage');
              const remaining = getMapIndex();
              if (remaining.length > 0) switchMap(remaining[0].id);
            }
          }

          // Modal solo per download e conflitti: l'utente deve decidere
          const needsUserInput = plan.toDownload.length + plan.conflicts.length > 0;
          if (needsUserInput) {
            setMergePlan(plan);
            setShowMergeModal(true);
          }
        })
        .catch(console.error);
    }
    prevUserRef.current = user;
  }, [user, currentMapId, refreshMaps, switchMap]);

  const handleMergeConfirm = useCallback(async (resolutions) => {
    if (!user || !mergePlan) return;
    try {
      await executeSyncPlan(mergePlan, resolutions, user.id);
      const cloudConflictAffectsCurrentMap = resolutions.some(
        r => r.choice === 'cloud' && r.conflict.local.id === currentMapId
      );
      if (cloudConflictAffectsCurrentMap) reloadCurrentMap();
      refreshMaps();
    } catch (err) {
      console.error('Sync failed:', err);
    } finally {
      setShowMergeModal(false);
      setMergePlan(null);
    }
  }, [user, mergePlan, currentMapId, reloadCurrentMap, refreshMaps]);

  return { mergePlan, showMergeModal, setShowMergeModal, setMergePlan, handleMergeConfirm };
}
